// Measures how long each photograph's tile was visible on the moving wall ("wall exposure"), the fair denominator for
// tile clicks in the photo insights report. A one-second timer samples which rendered tiles intersect the viewport and
// adds one second per visible image; the totals are sent once a minute and when the page is hidden or unloaded. The
// sampler is pure (clock, wall state, identity and transport are injected) so it can be tested without a browser, and
// it swallows its own failures: telemetry must never throw into the gallery code.
import type { PhotoInsightsIdentity } from './photo-insights';
import { isRectangleVisible } from './site-animation';
import type { Rectangle } from './site-animation';

export const photoExposureEndpoint = '/api/telemetry/photo-exposure';
export const exposureSampleIntervalMilliseconds = 1_000;
export const exposureFlushIntervalMilliseconds = 60_000;
/** A flush sends at most this many photographs, the ones seen longest; the server rejects larger requests. */
export const maximumExposureEntriesPerFlush = 200;

export type PhotoExposureEntry = {
    imageKey: string;
    seconds: number;
};

export type PhotoExposureRecord = {
    browserId: string;
    sessionId: string;
    exposures: PhotoExposureEntry[];
};

/** One rendered wall tile. The rectangle is read lazily, only for titled photographs not yet counted in a sample. */
export type WallExposureTile = {
    imageKey: string;
    title: string | null | undefined;
    getRectangle: () => Rectangle;
};

export type WallExposureDependencies = {
    /** A monotonic clock in milliseconds, normally performance.now(). */
    now: () => number;
    /** True while the homepage wall is on screen: the home route, a visible document, and no photograph presented. */
    isWallShowing: () => boolean;
    getTiles: () => Iterable<WallExposureTile>;
    getViewportSize: () => { width: number; height: number };
    getIdentity: () => PhotoInsightsIdentity;
    send: (record: PhotoExposureRecord) => void;
};

export type WallExposureSampler = {
    /** Runs once a second: samples the wall when it is showing, then flushes once a minute has passed. */
    tick: () => void;
    /** Sends the accumulated seconds, if any, and starts a new interval. */
    flush: () => void;
    /** The number of photographs with unsent seconds. */
    readonly pendingCount: number;
};

/** The flush triggers: the sampling timer, page unload, and the page becoming hidden. */
export type WallExposureEnvironment = {
    setInterval: (callback: () => void, milliseconds: number) => unknown;
    window: Pick<EventTarget, 'addEventListener'>;
    document: Pick<EventTarget, 'addEventListener'> & { readonly hidden: boolean };
};

/** Only titled photographs have public photo pages, and the server accepts exposure only for those. */
export function hasPublicTitle(title: string | null | undefined): boolean {
    return title != null && title.trim().length > 0;
}

export function createWallExposureSampler(dependencies: WallExposureDependencies): WallExposureSampler {
    const pending = new Map<string, number>();
    let intervalStartedAt = safeNow(dependencies);

    function sample(): void {
        if (!dependencies.isWallShowing()) return;
        const viewport = dependencies.getViewportSize();
        // A photograph repeated on the looping wall counts once per sample, however many of its tiles are visible.
        const counted = new Set<string>();
        for (const tile of dependencies.getTiles()) {
            if (counted.has(tile.imageKey) || !hasPublicTitle(tile.title)) continue;
            if (!isRectangleVisible(tile.getRectangle(), viewport.width, viewport.height)) continue;
            counted.add(tile.imageKey);
            pending.set(tile.imageKey, (pending.get(tile.imageKey) ?? 0) + 1);
        }
    }

    function flush(): void {
        try {
            const now = dependencies.now();
            const elapsedMilliseconds = now - intervalStartedAt;
            intervalStartedAt = now;
            if (pending.size === 0) return;
            // No photograph can have been visible for longer than the interval being reported.
            const limit = Math.max(1, Math.ceil(elapsedMilliseconds / 1_000));
            const exposures = [...pending]
                .map(([imageKey, seconds]) => ({ imageKey, seconds: Math.min(seconds, limit) }))
                .sort((left, right) => right.seconds - left.seconds || compareKeys(left.imageKey, right.imageKey))
                .slice(0, maximumExposureEntriesPerFlush);
            pending.clear();
            const identity = dependencies.getIdentity();
            dependencies.send({ browserId: identity.browserId, sessionId: identity.sessionId, exposures });
        } catch {
            pending.clear();
            // Telemetry must never interfere with the public site.
        }
    }

    return {
        tick() {
            try {
                // An interval starts with its first sample, so idle time (another page, an open photograph with
                // nothing pending) neither triggers an early flush nor loosens the clamp.
                if (pending.size === 0) intervalStartedAt = dependencies.now() - exposureSampleIntervalMilliseconds;
                sample();
                if (dependencies.now() - intervalStartedAt >= exposureFlushIntervalMilliseconds) flush();
            } catch {
                // Telemetry must never interfere with the public site.
            }
        },
        flush,
        get pendingCount() {
            return pending.size;
        }
    };
}

/** Starts the one-second sampling timer and flushes on pagehide and when the page becomes hidden. */
export function installWallExposureSampler(sampler: WallExposureSampler, environment: WallExposureEnvironment): void {
    try {
        environment.setInterval(() => sampler.tick(), exposureSampleIntervalMilliseconds);
        environment.window.addEventListener('pagehide', () => sampler.flush());
        environment.document.addEventListener('visibilitychange', () => {
            if (environment.document.hidden) sampler.flush();
        });
    } catch {
        // Telemetry must never interfere with the public site.
    }
}

/** Posts one exposure record without waiting, surviving page unload. */
export function sendPhotoExposure(record: PhotoExposureRecord): void {
    try {
        void fetch(photoExposureEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            keepalive: true,
            body: JSON.stringify(record)
        }).catch(() => {
            // Telemetry must never interfere with the public site.
        });
    } catch {
        // fetch can throw synchronously, for example when the keepalive quota is exhausted.
    }
}

function safeNow(dependencies: WallExposureDependencies): number {
    try {
        return dependencies.now();
    } catch {
        return 0;
    }
}

function compareKeys(left: string, right: string): number {
    return left < right ? -1 : left > right ? 1 : 0;
}
